import islandRubyShowcaseImg from '@/assets/images/firstProduct/test-one-1-e1686990967731.jpg'
import giftBoxShowcaseImg from '@/assets/images/firstProduct/02-禮盒-2.png'
import islandRubyTeaBagShowcaseImg from '@/assets/images/firstProduct/IR-TB.png'
import { SHOP_PRODUCTS, TEA_PRODUCT_NAMES, TEA_PRODUCTS } from '@/constants/products'
import type { TeaProduct } from '@/types/common'
import type { AuthResponse } from '@/types/auth'
import type { Order } from './orders.api'
import type { PaymentVerification } from './payments.api'
import type { UploadedImage } from './uploads.api'

interface ProductImage {
  id: string
  url: string
  alt: string
  isPrimary: boolean
  sortOrder: number
}

export interface MockProductListItem {
  id: string
  key: TeaProduct['key']
  name?: string
  price: number
  category?: string
  format?: string
  stock?: number
  primaryImageId?: string
  primaryImageUrl?: string
}

export interface MockProductRecord extends MockProductListItem {
  description?: string
  images: ProductImage[]
  sku?: string
}

export interface MockState {
  catalogProducts: MockProductRecord[]
  featuredProducts: MockProductListItem[]
  showcaseProducts: MockProductListItem[]
  orders: Order[]
  payments: PaymentVerification[]
  uploads: UploadedImage[]
  authResponse: AuthResponse
}

const toRouteId = (product: Pick<TeaProduct, 'id' | 'key' | 'format'>): string =>
  product.id || (product.format ? `${product.key}-${product.format}` : product.key)

const createProductImages = (id: string, product: TeaProduct): ProductImage[] => {
  const images = product.images?.length ? product.images : [product.image]

  return images.map((imageUrl, index) => ({
    id: `img-${id}-${index + 1}`,
    url: imageUrl,
    alt: TEA_PRODUCT_NAMES[product.key].en,
    isPrimary: index === 0,
    sortOrder: index,
  }))
}

const createCatalogProduct = (product: TeaProduct): MockProductRecord => {
  const id = toRouteId(product)
  const images = createProductImages(id, product)
  const primaryImage = images[0]

  return {
    id,
    key: product.key,
    price: product.price,
    category: product.category,
    format: product.format,
    stock: product.stock,
    primaryImageId: primaryImage.id,
    primaryImageUrl: primaryImage.url,
    description: product.description,
    images,
    sku: product.sku,
  }
}

const createFeaturedProduct = (product: TeaProduct): MockProductListItem => ({
  id: product.id || product.key,
  key: product.key,
  price: product.price,
  primaryImageId: `img-${product.key}`,
  primaryImageUrl: product.image,
})

const showcaseProducts: MockProductListItem[] = [
  {
    id: 'i-loose-leaf',
    key: 'i',
    price: 30.0,
    format: 'loose-leaf',
    primaryImageId: 'img-showcase-i-loose-leaf',
    primaryImageUrl: islandRubyShowcaseImg,
  },
  {
    id: 'g-gift-sets',
    key: 'g',
    price: 43.5,
    category: 'gift-sets',
    format: 'gift-sets',
    primaryImageId: 'img-showcase-g-gift-sets',
    primaryImageUrl: giftBoxShowcaseImg,
  },
  {
    id: 'i-tea-bags',
    key: 'i',
    price: 26.0,
    format: 'tea-bags',
    primaryImageId: 'img-showcase-i-tea-bags',
    primaryImageUrl: islandRubyTeaBagShowcaseImg,
  },
]

const now = new Date().toISOString()

export const createInitialMockState = (): MockState => ({
  catalogProducts: SHOP_PRODUCTS.map(createCatalogProduct),
  featuredProducts: TEA_PRODUCTS.map(createFeaturedProduct),
  showcaseProducts,
  orders: [],
  payments: [],
  uploads: [],
  authResponse: {
    userId: 'mock-user-001',
    email: 'guest@cloutyspace.mock',
    name: 'CloutySpace Guest',
    userName: 'cloutyspace_guest',
    picture: 'https://ui-avatars.com/api/?name=CloutySpace&background=F2E7D9&color=6B4E3D',
    memberLevel: 1,
    isRegister: true,
    authProvider: 'mock',
    phoneNumber: '',
    accessToken: 'mock-access-token',
    expiresIn: 3600,
    lastLoginAt: now,
  },
})
